import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slider from "@mui/material/Slider";
import FormControl from "@mui/material/FormControl"; // Added
import InputLabel from "@mui/material/InputLabel"; // Added
import Select from "@mui/material/Select"; // Added
import MenuItem from "@mui/material/MenuItem"; // Added
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { Edit2, Check, X } from "lucide-react";

export default function SleepCoachDoneDialogue({
  open,
  handleClose,
  message = "",
  heading = "",
  isLoading = false,
}) {
  const { t } = useTranslation();
  const [avgSleepDuration, setAvgSleepDuration] = React.useState(6 * 60); // Store in minutes

  // 🛑 UPDATED STATE: Replaced 'wakeUp' boolean with 'wokeUpTimes' string
  const [wokeUpTimes, setWokeUpTimes] = React.useState("no_wakeup");

  const [isEditing, setIsEditing] = React.useState(false);
  const [hoursInput, setHoursInput] = React.useState("6");
  const [minutesInput, setMinutesInput] = React.useState("0");

  // 🛑 DEFINED OPTIONS
  const wakeUpOptions = [
    { value: "no_wakeup", label: t("sleepCoachPreferences.no_wakeup") },
    { value: "1-2_times", label: t("sleepCoachPreferences.1-2_times") },
    { value: "2-3_times", label: t("sleepCoachPreferences.2-3_times") },
    { value: "3-4_times", label: t("sleepCoachPreferences.3-4_times") },
    {
      value: "more_than_4_times",
      label: t("sleepCoachPreferences.more_than_4_times"),
    },
  ];

  // Reset values when dialog opens
  React.useEffect(() => {
    if (open) {
      setAvgSleepDuration(6 * 60);
      setWokeUpTimes("no_wakeup"); // 🛑 UPDATED RESET
      setIsEditing(false);
      setHoursInput("6");
      setMinutesInput("0");
    }
  }, [open]);

  // Update inputs when slider changes
  React.useEffect(() => {
    const hours = Math.floor(avgSleepDuration / 60);
    const minutes = avgSleepDuration % 60;
    setHoursInput(hours.toString());
    setMinutesInput(minutes.toString());
  }, [avgSleepDuration]);

  // Convert minutes to formatted string
  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (mins === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${mins}min`;
    }
  };

  const handleSliderChange = (event, newValue) => {
    setAvgSleepDuration(newValue);
  };

  // 🛑 NEW HANDLER FOR SELECT
  const handleWokeUpTimesChange = (event) => {
    setWokeUpTimes(event.target.value);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const hours = parseInt(hoursInput) || 0;
    const minutes = parseInt(minutesInput) || 0;

    // Validate inputs
    if (hours < 0 || hours > 12) {
      alert("Hours must be between 0 and 12");
      return;
    }
    if (minutes < 0 || minutes > 59) {
      alert("Minutes must be between 0 and 59");
      return;
    }

    const totalMinutes = hours * 60 + minutes;
    if (totalMinutes < 60 || totalMinutes > 720) {
      alert("Total sleep duration must be between 1 hour and 12 hours");
      return;
    }

    setAvgSleepDuration(totalMinutes);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    // Reset inputs to current slider value
    const hours = Math.floor(avgSleepDuration / 60);
    const minutes = avgSleepDuration % 60;
    setHoursInput(hours.toString());
    setMinutesInput(minutes.toString());
    setIsEditing(false);
  };

  const handleHoursChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setHoursInput(value);
  };

  const handleMinutesChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setMinutesInput(value);
  };

  const handleConfirm = () => {
    // 🛑 UPDATED PAYLOAD: Using the requested key `wake_up` but passing the new string value
    handleClose({
      avg_sleep_duration: formatTime(avgSleepDuration),
      avg_sleep_duration_hours: avgSleepDuration / 60,
      wake_up: wokeUpTimes, // 🛑 CHANGED: Passing the selected string option
    });
  };

  const handleCancel = () => {
    handleClose();
  };

  // Custom value label formatter
  const valueLabelFormat = (value) => {
    return formatTime(value);
  };

  // Generate marks for the slider (every hour)
  const marks = Array.from({ length: 12 }, (_, i) => ({
    value: (i + 1) * 60,
    label: `${i + 1}h`,
  }));

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title" className="text-blue-600">
        {heading}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description" sx={{ mb: 3 }}>
          {message}
        </DialogContentText>

        {/* Sleep Duration Slider and Edit */}
        <Box sx={{ px: 2 }}>
          <Typography
            id="sleep-duration-slider"
            gutterBottom
            className="!font-[600]"
          >
            {t("sleepCoachPreferences.Sleep Duration")}
          </Typography>

          {/* Selected Value Display with Edit Option */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              gap: 1,
              minHeight: "40px",
            }}
          >
            {isEditing ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <input
                    type="text"
                    value={hoursInput}
                    onChange={handleHoursChange}
                    className="w-12 border border-gray-300 rounded px-2 py-1 text-center text-lg font-semibold"
                    maxLength={2}
                    placeholder="6"
                  />
                  <span className="text-gray-600">h</span>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <input
                    type="text"
                    value={minutesInput}
                    onChange={handleMinutesChange}
                    className="w-12 border border-gray-300 rounded px-2 py-1 text-center text-lg font-semibold"
                    maxLength={2}
                    placeholder="0"
                  />
                  <span className="text-gray-600">min</span>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, ml: 1 }}>
                  <button
                    onClick={handleSaveEdit}
                    className="p-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  >
                    <Check size={16} />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#0575e6",
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                  }}
                >
                  {formatTime(avgSleepDuration)}
                </Typography>
                {/* <button
                  onClick={handleEditClick}
                  className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Edit time manually"
                >
                  <Edit2 size={16} />
                </button> */}
              </Box>
            )}
          </Box>

          <Slider
            value={avgSleepDuration}
            onChange={handleSliderChange}
            aria-labelledby="sleep-duration-slider"
            valueLabelDisplay="auto"
            valueLabelFormat={valueLabelFormat}
            step={15} // 15-minute increments
            marks={marks}
            min={60} // 1 hour in minutes
            max={720} // 12 hours in minutes
            sx={{
              color: "#0575e6",
              "& .MuiSlider-thumb": {
                backgroundColor: "#fff",
                border: "2px solid #0575e6",
                width: 20,
                height: 20,
                "&:hover": {
                  boxShadow: "0px 0px 0px 8px rgba(5, 117, 230, 0.16)",
                },
              },
              "& .MuiSlider-valueLabel": {
                backgroundColor: "#0575e6",
                color: "#fff",
                fontSize: "0.75rem",
              },
              "& .MuiSlider-track": {
                height: 4,
              },
              "& .MuiSlider-rail": {
                height: 4,
                opacity: 0.3,
              },
              "& .MuiSlider-mark": {
                height: 8,
                width: 2,
                backgroundColor: "#0575e6",
                opacity: 0.4,
              },
              "& .MuiSlider-markActive": {
                opacity: 1,
              },
              "& .MuiSlider-markLabel": {
                fontSize: "0.75rem",
                color: "#666",
                marginTop: "8px",
              },
            }}
          />
        </Box>

        {/* 🛑 WAKE UP SELECT INPUT */}
        <Box sx={{ mt: 3, px: 2 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              id="wake-up-select-label"
              size="small"
              sx={{
                "&.Mui-focused": { color: "#0575e6" },
              }}
            >
              {t("sleepCoachPreferences.Woke up during sleep?")}
            </InputLabel>
            <Select
              labelId="wake-up-select-label"
              value={wokeUpTimes}
              onChange={handleWokeUpTimesChange}
              label={t("sleepCoachPreferences.Woke up during sleep?")}
              size="small"
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.5)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#0575e6",
                },
              }}
            >
              {wakeUpOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleCancel}
          disabled={isLoading}
          variant="outlined"
          color="error"
          sx={{
            borderRadius: "6px",
            textTransform: "none",
            ...(isLoading && { opacity: 0.7 }),
          }}
        >
          {t(`firstFormSteps.confirmAdvisebuttonCancelText`) || "Cancel"}
        </Button>
        <button
          onClick={handleConfirm}
          disabled={isLoading || isEditing}
          className={`px-4 py-1 bg-blue-600 text-white rounded-md border border-blue-600 text-[14px] min-h-[36.5px] ${
            isLoading || isEditing
              ? "opacity-[0.7] pointer-events-none"
              : "hover:bg-white hover:text-[#0575E6]"
          }`}
        >
          {t(`firstFormSteps.confirmAdvisebuttonText`) || "Confirm"}
        </button>
      </DialogActions>
    </Dialog>
  );
}
